import React, { useEffect, useMemo, useState, useId, createElement } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useTranslation } from "../context/TranslationContext";
import { FileText, Book, ArrowRight, CheckCircle2, Clock, Circle, ArrowLeft, Key, RefreshCw, Loader2, Trash2 } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { GlossaryDrawer } from "../components/GlossaryDrawer";
import { syncTranslationsToFrTemplate, segmentsToInjectionTexts } from "../data/lexicalParser";
import { fetchTemplateBodies, updateTemplateBody } from "../data/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { SegmentStatusBadge } from "../components/SegmentStatusBadge";
import { SyncReportModal } from "../components/SyncReportModal";
export const TranslationHub: React.FC = () => {
  const {
    docId
  } = useParams<{
    docId: string;
  }>();
  const {
    segments,
    documentTitle,
    setCurrentDocumentId,
    currentDocument,
    isLoading,
    openRouterApiKey,
    clearAllTranslations,
    refreshDocuments,
    syncDocumentFromServer,
    lastSyncReport
  } = useTranslation();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showSyncReport, setShowSyncReport] = useState(false);
  const handleRefreshFromServer = async () => {
    if (!docId) return;
    setIsRefreshing(true);
    try {
      const stats = await syncDocumentFromServer(docId);
      if (!stats) {
        toast.success("Document refreshed from server.");
        return;
      }
      setShowSyncReport(true);
      const parts: string[] = [];
      if (stats.newSections) parts.push(`${stats.newSections} new`);
      if (stats.changedSections) parts.push(`${stats.changedSections} changed`);
      if (stats.requiresReview) parts.push(`${stats.requiresReview} require review`);
      if (stats.removedSections) parts.push(`${stats.removedSections} removed`);
      if (stats.approvedPreserved) parts.push(`${stats.approvedPreserved} approved preserved`);
      if (parts.length) {
        toast.success(`Synced from server — ${parts.join(", ")}.`);
      } else {
        toast.success("Already up to date — no changes detected.");
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to refresh from server");
    } finally {
      setIsRefreshing(false);
    }
  };
  const navigate = useNavigate();
  const [isGlossaryOpen, setIsGlossaryOpen] = useState(false);
  useEffect(() => {
    if (docId) {
      setCurrentDocumentId(docId);
    }
  }, [docId, setCurrentDocumentId]);
  const approvedCount = segments.filter(s => s.status === "approved").length;
  const totalCount = segments.length;
  const progress = totalCount > 0 ? Math.round(approvedCount / totalCount * 100) : 0;
  const isComplete = approvedCount === totalCount && totalCount > 0;
  const firstIncomplete = segments.find(s => s.status !== "approved" && s.status !== "removed");
  const getStatusIcon = (status: string) => {
    switch (status) {
      case "approved":
        return <CheckCircle2 className="w-5 h-5 text-emerald-500" data-id="element-813" />;
      case "in_progress":
        return <Clock className="w-5 h-5 text-amber-500" data-id="element-814" />;
      case "requires_review":
        return <Clock className="w-5 h-5 text-orange-500" data-id="element-815" />;
      case "new":
        return <Circle className="w-5 h-5 text-sky-500" data-id="element-816" />;
      case "removed":
        return <Circle className="w-5 h-5 text-red-300" data-id="element-817" />;
      default:
        return <Circle className="w-5 h-5 text-slate-300" data-id="element-818" />;
    }
  };
  const [isSyncing, setIsSyncing] = useState(false);
  const handleSync = async () => {
    if (!currentDocument) return;
    const targetTemplateId = currentDocument.frTemplateId;
    const enTemplateId = currentDocument.templateId;
    if (!targetTemplateId) {
      toast.error("No French template found to sync to. This document has no paired FR template.");
      return;
    }
    if (!enTemplateId) {
      toast.error("No English template found for this document.");
      return;
    }
    const translatedTexts = segmentsToInjectionTexts(segments);
    setIsSyncing(true);
    try {
      const {
        enBody,
        frBody
      } = await fetchTemplateBodies(enTemplateId, targetTemplateId);
      if (!enBody?.trim() || !frBody?.trim()) {
        toast.error("Could not load English and French template bodies from the server.");
        return;
      }
      const {
        body: newBody,
        warnings
      } = syncTranslationsToFrTemplate(enBody, frBody, translatedTexts);
      for (const warning of warnings) {
        console.warn(`[sync] ${warning}`);
      }
      if (warnings.length > 0) {
        toast.warning(warnings[0], {
          duration: 10000
        });
      }
      await updateTemplateBody(targetTemplateId, newBody);
      toast.success(`Synced translations to FR template #${targetTemplateId}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to sync translations");
    } finally {
      setIsSyncing(false);
    }
  };
  if (!docId || isLoading && !currentDocument) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-id="element-819">
        <div className="text-center" data-id="element-820">
          <div className="w-8 h-8 border-2 border-indigo-600/30 border-t-indigo-600 rounded-full animate-spin mx-auto mb-3" data-id="element-821" />
          <p className="text-sm text-slate-500" data-id="element-822">Loading document...</p>
        </div>
      </div>;
  }
  if (segments.length === 0) {
    return <div className="min-h-screen bg-slate-50 p-8" data-id="element-823">
        <div className="max-w-5xl mx-auto" data-id="element-824">
          <Link to="/translation" className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors mb-8" data-id="element-825">
            <ArrowLeft className="w-4 h-4" data-id="element-826" />
            Back to Documents
          </Link>
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center" data-id="element-827">
            <p className="text-slate-500" data-id="element-828">
              No segments found in this document.
            </p>
          </div>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-slate-50 p-8" data-id="element-829">
      <div className="max-w-5xl mx-auto space-y-8" data-id="element-830">
        <Link to="/translation" className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors" data-id="element-831">
          <ArrowLeft className="w-4 h-4" data-id="element-832" />
          Back to Documents
        </Link>

        {/* Header Section */}
        <div className="bg-white p-6 sm:p-8 rounded-xl border border-slate-200 shadow-sm" data-id="element-833">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6" data-id="element-834">
            {/* Left: title block */}
            <div className="min-w-0 flex-1" data-id="element-835">
              <div className="flex items-center gap-3 mb-3 flex-wrap" data-id="element-836">
                <div className="p-2 bg-indigo-50 rounded-lg shrink-0" data-id="element-837">
                  <FileText className="w-6 h-6 text-indigo-600" data-id="element-838" />
                </div>
                <div className="inline-flex items-center gap-1.5 text-xs font-semibold tracking-wide" data-id="element-839">
                  <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded" data-id="element-840">
                    EN
                  </span>
                  <ArrowRight className="w-3 h-3 text-slate-400" data-id="element-841" />
                  <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded" data-id="element-842">
                    FR
                  </span>
                </div>
                <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium ${openRouterApiKey ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`} title={openRouterApiKey ? "OpenRouter API key configured" : "No API key — translations will fail"} data-id="element-843">
                  <Key className="w-3 h-3" data-id="element-844" />
                  {openRouterApiKey ? "API Key" : "No API Key"}
                </span>
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-1.5 leading-tight" data-id="element-845">
                {documentTitle}
              </h1>
              <p className="text-slate-500 text-sm" data-id="element-846">
                {currentDocument?.createdAt ? new Date(currentDocument.createdAt).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric"
              }) : ""}
                {currentDocument?.createdAt && " • "}
                {totalCount} segments
              </p>
            </div>

            {/* Right: action bar */}
            <div className="flex items-center gap-2 flex-wrap lg:flex-nowrap lg:justify-end shrink-0" data-id="element-847">
              <button onClick={handleRefreshFromServer} disabled={isRefreshing} className="inline-flex items-center justify-center gap-2 px-3.5 py-2.5 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap" title="Re-fetch the English document from the server and detect added, edited, or removed sections" data-id="element-848">
                {isRefreshing ? <Loader2 className="w-4 h-4 animate-spin" data-id="element-849" /> : <RefreshCw className="w-4 h-4" data-id="element-850" />}
                <span data-id="element-851">{isRefreshing ? "Syncing…" : "Sync from Server"}</span>
              </button>

              <button onClick={() => setIsGlossaryOpen(true)} className="inline-flex items-center justify-center gap-2 px-3.5 py-2.5 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-colors whitespace-nowrap" data-id="element-852">
                <Book className="w-4 h-4" data-id="element-853" />
                <span data-id="element-854">Glossary</span>
              </button>

              <button onClick={() => {
              if (window.confirm("Clear all saved translations and draft progress? This cannot be undone.")) {
                clearAllTranslations();
                toast.success("All translations cleared");
              }
            }} className="inline-flex items-center justify-center w-10 h-10 bg-white border border-slate-200 rounded-lg text-slate-500 hover:text-red-600 hover:bg-red-50 hover:border-red-200 transition-colors" title="Clear all translations" aria-label="Clear all translations" data-id="element-855">
                <Trash2 className="w-4 h-4" data-id="element-856" />
              </button>

              {!isComplete && <button onClick={() => navigate(`/document/${docId}/translate/${firstIncomplete?.id}`)} className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors shadow-sm whitespace-nowrap" data-id="element-857">
                  <span data-id="element-858">Continue Translation</span>
                  <ArrowRight className="w-4 h-4" data-id="element-859" />
                </button>}
            </div>
          </div>
        </div>

        {/* Progress Section */}
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm" data-id="element-860">
          <div className="flex justify-between items-end mb-4" data-id="element-861">
            <div data-id="element-862">
              <h2 className="text-lg font-semibold text-slate-900" data-id="element-863">
                Translation Progress
              </h2>
              <p className="text-sm text-slate-500" data-id="element-864">
                {approvedCount} of {totalCount} segments approved
              </p>
            </div>
            <div className="text-2xl font-bold text-indigo-600" data-id="element-865">
              {progress}%
            </div>
          </div>
          <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden" data-id="element-866">
            <div className="bg-indigo-600 h-3 rounded-full transition-all duration-1000 ease-out" style={{
            width: `${progress}%`
          }} data-id="element-867"></div>
          </div>

          {isComplete && <div className="mt-6 p-4 bg-emerald-50 border border-emerald-100 rounded-lg flex items-center justify-between gap-4" data-id="element-868">
              <div className="flex items-center gap-3 text-emerald-800" data-id="element-869">
                <CheckCircle2 className="w-6 h-6 text-emerald-500" data-id="element-870" />
                <div data-id="element-871">
                  <p className="font-medium" data-id="element-872">Translation Complete</p>
                  <p className="text-sm text-emerald-600/80" data-id="element-873">
                    All segments approved.{" "}
                    {currentDocument?.frTemplateId ? `Ready to sync to FR template #${currentDocument.frTemplateId}.` : "No paired FR template found."}
                  </p>
                </div>
              </div>
              <button onClick={handleSync} disabled={isSyncing || !currentDocument?.frTemplateId} className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm shrink-0" data-id="element-874">
                {isSyncing ? <Loader2 className="w-4 h-4 animate-spin" data-id="element-875" /> : <RefreshCw className="w-4 h-4" data-id="element-876" />}
                {isSyncing ? "Syncing..." : "Sync to Document Hub"}
              </button>
            </div>}
        </div>

        {/* Segments List */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-id="element-877">
          <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50" data-id="element-878">
            <h3 className="font-semibold text-slate-900" data-id="element-879">Document Segments</h3>
          </div>
          <div className="divide-y divide-slate-100" data-id="element-880">
            {segments.map(segment => <div key={segment.id} className="p-4 sm:px-6 hover:bg-slate-50 transition-colors flex items-start gap-4 group cursor-pointer" onClick={() => navigate(`/document/${docId}/translate/${segment.id}`)} data-id="element-881">
                <div className="mt-1" data-id="element-882">{getStatusIcon(segment.status)}</div>
                <div className="flex-1 min-w-0" data-id="element-883">
                  <div className="flex items-center justify-between mb-1 gap-2" data-id="element-884">
                    <span className="text-xs font-semibold text-slate-500 tracking-wider uppercase" data-id="element-885">
                      {segment.clauseId}
                    </span>
                    <div className="flex items-center gap-2" data-id="element-886">
                      {segment.locked && <span className="text-[10px] font-medium text-slate-400 uppercase" data-id="element-887">
                          Locked
                        </span>}
                      <SegmentStatusBadge status={segment.status} data-id="element-888" />
                    </div>
                  </div>
                  <p className="text-sm text-slate-900 line-clamp-2 font-legal" data-id="element-889">
                    {segment.sourceText}
                  </p>
                </div>
                <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center self-center text-indigo-600" data-id="element-890">
                  <ArrowRight className="w-5 h-5" data-id="element-891" />
                </div>
              </div>)}
          </div>
        </div>
      </div>

      <GlossaryDrawer isOpen={isGlossaryOpen} onClose={() => setIsGlossaryOpen(false)} data-id="element-892" />
      <SyncReportModal isOpen={showSyncReport} onClose={() => setShowSyncReport(false)} report={lastSyncReport} documentTitle={documentTitle} data-id="element-893" />
    </div>;
};